/* Embroidery Inc homepage redesign 2026-10: proof wall (sections/ei-proof-wall.liquid).
   Vanilla, deferred, no dependencies. Does nothing at page load except watch for the section:
   all work (reading the photo list, building the drifting columns, loading images) starts when
   the wall comes within about one screen of the viewport, in idle-time chunks.
   - drift: columns of tiles glide up or down at different slow speeds (Web Animations,
     transform only, at least 40 s per loop). The wall is ambient: tiles are plain images and
     the pointer never slows or stops it. It rests off screen, in a hidden tab and while the
     viewer is open, and the small pause button (WCAG 2.2.2) is the only visible way to stop it;
   - develop-in: tiles settle in with a short wave the first time the wall scrolls into view;
   - viewer: <dialog> lightbox opened by "Browse all" at photo 1, with arrows, swipe, Esc,
     counter and neighbor preloading over every photo in the set (the wall shows a subset).
   Reduced motion, Save-Data or animation turned off: static grid, viewer still works. */
(function () {
  'use strict';

  var SPEED_MIX = [1, 0.8, 1.14, 0.88, 1.06, 0.84, 1.18, 0.92, 1.1, 0.86];
  var MIN_LOOP_MS = 40000;
  var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var idle = window.requestIdleCallback
    ? function (cb) { return window.requestIdleCallback(cb, { timeout: 700 }); }
    : function (cb) { return setTimeout(function () { cb({ didTimeout: true, timeRemaining: function () { return 6; } }); }, 40); };

  function prefersStatic() {
    var c = navigator.connection;
    return !!((reduceMq && reduceMq.matches) || (c && c.saveData));
  }

  function fill(tpl, n, total) {
    return String(tpl || '').replace('[n]', n).replace('[total]', total);
  }

  // Run fn over list in idle slices (at most `cap` items per slice), then done().
  function inIdle(list, fn, done, cap) {
    var i = 0;
    var step = function (deadline) {
      var n = 0;
      while (i < list.length && n < (cap || 12) && (deadline.didTimeout || deadline.timeRemaining() > 3)) { fn(list[i], i); i++; n++; }
      if (i < list.length) idle(step); else done();
    };
    idle(step);
  }

  function ProofWall(root) {
    var self = this;
    this.root = root;
    this.wall = root.querySelector('[data-ei-pw-wall]');
    this.grid = root.querySelector('[data-ei-pw-grid]');
    this.dialog = root.querySelector('[data-ei-pw-lb]');
    this.speed = parseFloat(root.getAttribute('data-speed')) || 14;
    this.items = [];
    this.cells = [];
    this.tracks = [];
    this.colsEl = null;
    this.animated = false;
    this.ready = false;
    this.toggle = root.querySelector('[data-ei-pw-toggle]');
    this.state = { visible: false, hidden: document.hidden, paused: false, viewer: false, started: false };
    this.rate = 0;
    this.target = 0;
    this.raf = 0;
    this.index = 0;
    this.opener = null;
    if (!this.wall || !this.grid) return;

    // The only page-load work: one observer, plus a focus catch in case a keyboard user
    // reaches the section's buttons before the observer fires.
    var kick = function () { self.prepare(); };
    if ('IntersectionObserver' in window) {
      this.nearIO = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) { self.nearIO.disconnect(); kick(); }
      }, { rootMargin: '100% 0px 100% 0px' });
      this.nearIO.observe(root);
    } else {
      idle(kick);
    }
    root.addEventListener('focusin', kick);
  }

  // Read the photo list and wire the viewer, in idle time.
  ProofWall.prototype.prepare = function () {
    var self = this;
    if (this.ready) return;
    if (this.nearIO) this.nearIO.disconnect();
    if (this.preparing) return;
    this.preparing = true;
    idle(function () { if (!self.ready) self.setup(); });
  };

  ProofWall.prototype.setup = function () {
    var self = this;
    var root = this.root;
    this.ready = true;

    var data = {};
    try { data = JSON.parse(root.querySelector('[data-ei-pw-data]').textContent) || {}; } catch (e) { data = {}; }
    this.data = data;

    // Fade images in as they load (capture: load does not bubble).
    var mark = function (e) { if (e.target && e.target.tagName === 'IMG') e.target.classList.add('is-ld'); };
    root.addEventListener('load', mark, true);
    root.addEventListener('error', mark, true);

    Array.prototype.forEach.call(this.grid.children, function (cell) {
      var tile = cell.querySelector('.ei-pw__tile');
      var img = tile && tile.querySelector('img');
      if (!img) return;
      if (img.complete && img.naturalWidth) img.classList.add('is-ld');
      self.items.push({ s: img.getAttribute('src'), srcset: img.getAttribute('srcset'), l: tile.getAttribute('data-large'), a: img.alt });
      self.cells.push(cell);
    });
    root.classList.add('is-js');
    this.sizesAttr = (this.cells[0] && this.cells[0].querySelector('img').getAttribute('sizes')) || '33vw';

    // The rest of the set, for the viewer (and for wall slots past the server-rendered tiles).
    var total = data.total || 0;
    var sizes = data.sizes || ['360', '540', '1080'];
    var alts = data.alts || {};
    (data.items || []).forEach(function (it) {
      var n = self.items.length + 1;
      if (Array.isArray(it)) {
        var stem = (data.base || '') + (data.prefix || 'ei-proof-') + it[0] + '-';
        var type = (data.types || '').charAt(parseInt(it[0], 10) - 1) || 'o';
        var small = stem + sizes[0] + '.webp?v=' + it[1];
        self.items.push({
          s: small,
          srcset: small + ' ' + sizes[0] + 'w, ' + stem + sizes[1] + '.webp?v=' + it[2] + ' ' + sizes[1] + 'w',
          l: stem + sizes[2] + '.webp?v=' + it[3],
          a: fill(alts[type] || alts.o, n, total)
        });
      } else if (it && it.s) {
        self.items.push({ s: it.s, srcset: it.s + ' 360w, ' + it.m + ' 540w', l: it.l, a: it.a || fill(alts.o, n, total) });
      }
    });

    this.initViewer();
    var browse = root.querySelector('.ei-pw__browse');
    if (browse && this.dialog && this.dialog.showModal) browse.hidden = false;

    this.animated = !root.classList.contains('is-static') && !prefersStatic() &&
      'IntersectionObserver' in window && typeof Element.prototype.animate === 'function';
    if (!this.animated) {
      if (!root.classList.contains('is-static') && !(reduceMq && reduceMq.matches)) root.classList.add('is-static'); // Save-Data
      return;
    }
    this.buildIdle(function () { self.bindMotion(); });
  };

  ProofWall.prototype.makeCell = function (item) {
    var li = document.createElement('li');
    li.className = 'ei-pw__cell';
    var tile = document.createElement('div'); // a picture, not a control
    tile.className = 'ei-pw__tile';
    var img = document.createElement('img');
    img.width = 360;
    img.height = 360;
    img.decoding = 'async';
    img.alt = item.a || '';
    img.sizes = this.sizesAttr;
    // Real src only once the tile drifts near the visible band (see watchImages).
    img.setAttribute('data-ei-srcset', item.srcset);
    img.setAttribute('data-ei-src', item.s);
    tile.appendChild(img);
    li.appendChild(tile);
    return li;
  };

  // Loop copies: aria-hidden, and their image is deferred like any JS tile unless the
  // original has already finished loading (then it comes from cache).
  ProofWall.prototype.cloneCell = function (cell) {
    var c = cell.cloneNode(true);
    c.setAttribute('aria-hidden', 'true');
    c.setAttribute('data-ei-pw-clone', '');
    var im = c.querySelector('img');
    var orig = cell.querySelector('img');
    if (im) {
      im.alt = '';
      if (im.getAttribute('src') && !(orig && orig.complete && orig.naturalWidth)) {
        im.setAttribute('data-ei-src', im.getAttribute('src'));
        if (im.getAttribute('srcset')) im.setAttribute('data-ei-srcset', im.getAttribute('srcset'));
        im.removeAttribute('srcset');
        im.removeAttribute('src');
        im.classList.remove('is-ld');
      }
    }
    return c;
  };

  ProofWall.prototype.colCount = function () {
    var v = parseInt(getComputedStyle(this.root).getPropertyValue('--pw-cols'), 10);
    return v > 1 ? v : 3;
  };

  // How many photos the drifting wall uses: enough per column to cover the wall height once,
  // with a floor from CSS (--pw-per-col), so the DOM stays small on phones.
  ProofWall.prototype.slots = function (cols) {
    var per = parseInt(getComputedStyle(this.root).getPropertyValue('--pw-per-col'), 10) || 6;
    var unit = this.wall.clientWidth / Math.max(cols - 1, 1);
    var need = unit ? Math.ceil(this.wall.clientHeight / unit) + 1 : per;
    return Math.min(this.items.length, cols * Math.max(per, need));
  };

  // Create the JS tiles the wall needs in idle slices, then build in one frame.
  ProofWall.prototype.buildIdle = function (done) {
    var self = this;
    var want = this.slots(this.colCount());
    var todo = [];
    for (var i = this.cells.length; i < want; i++) todo.push(i);
    inIdle(todo, function (i) { self.cells[i] = self.makeCell(self.items[i]); }, function () {
      requestAnimationFrame(function () { self.build(); if (done) done(); });
    }, 10);
  };

  ProofWall.prototype.build = function () {
    var self = this;
    this.teardown();
    var cols = this.colCount();
    this.cols = cols;
    var want = this.slots(cols);
    for (var i = this.cells.length; i < want; i++) this.cells[i] = this.makeCell(this.items[i]);
    var use = this.cells.slice(0, want);
    // Server tiles the wall does not use (only when the CSS asks for fewer) stay out of the DOM.
    this.cells.slice(want).forEach(function (cell) { if (cell.parentNode) cell.parentNode.removeChild(cell); });

    // Arm the develop-in only while the wall is still below the fold (never hide what is on screen).
    if (!this.state.started) {
      this.armed = this.wall.getBoundingClientRect().top > (window.innerHeight || 800);
      this.root.classList.toggle('is-armed', this.armed);
    }

    var colsEl = document.createElement('div');
    colsEl.className = 'ei-pw__cols';
    var tracks = [];
    for (var c = 0; c < cols; c++) {
      var col = document.createElement('div');
      col.className = 'ei-pw__col';
      var ul = document.createElement('ul');
      ul.className = 'ei-pw__track';
      ul.setAttribute('role', 'list');
      col.appendChild(ul);
      colsEl.appendChild(col);
      tracks.push({ el: ul, cells: [], anim: null });
    }
    // Same round-robin order as the CSS grid rows, so the switch is invisible.
    use.forEach(function (cell, k) { tracks[k % cols].cells.push(cell); tracks[k % cols].el.appendChild(cell); });
    if (this.grid.parentNode) this.grid.parentNode.removeChild(this.grid);
    this.wall.insertBefore(colsEl, this.toggle && this.toggle.parentNode === this.wall ? this.toggle : null);
    this.colsEl = colsEl;
    this.tracks = tracks;

    var first = use[0];
    if (!first) return;
    var gap = parseFloat(getComputedStyle(first).marginBottom) || 0;
    var unit = first.getBoundingClientRect().height + gap;
    var view = this.wall.clientHeight;
    var waveCenter = (cols - 1) / 2;

    tracks.forEach(function (t, c) {
      var n = t.cells.length;
      if (!n || !unit) return;
      var originals = t.cells.slice();
      var setH = n * unit;
      var reps = 1;
      while (setH * reps < view + unit) { // short column: repeat it until it covers the wall
        originals.forEach(function (cell) { t.el.appendChild(self.cloneCell(cell)); });
        reps++;
      }
      var loop = setH * reps;
      var frag = document.createDocumentFragment();
      Array.prototype.forEach.call(t.el.children, function (cell) { frag.appendChild(self.cloneCell(cell)); });
      t.el.appendChild(frag);
      var pxPerSec = self.speed * SPEED_MIX[c % SPEED_MIX.length];
      var duration = Math.max(MIN_LOOP_MS, loop / pxPerSec * 1000);
      t.loop = loop;
      t.duration = duration;
      t.reverse = c % 2 === 1;
      t.anim = t.el.animate(
        [{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(0,-50%,0)' }],
        { duration: duration, iterations: Infinity, easing: 'linear', direction: t.reverse ? 'reverse' : 'normal' }
      );
      t.anim.pause();
      if (self.armed && !self.state.started) {
        var kids = t.el.children;
        for (var r = 0; r < Math.min(kids.length, 7); r++) {
          var delay = Math.round(Math.abs(c - waveCenter) * 90 + r * 120);
          kids[r].style.setProperty('--d', Math.min(delay, 1300) + 'ms');
        }
      }
    });
    this.applyRate(this.rate);
    this.watchImages();
  };

  // Deferred tiles load their image only when they come within half a wall of the visible band.
  ProofWall.prototype.watchImages = function () {
    var self = this;
    if (!this.lazyIO) {
      this.lazyIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var img = e.target;
          self.lazyIO.unobserve(img);
          if (img.getAttribute('data-ei-srcset')) img.srcset = img.getAttribute('data-ei-srcset');
          if (img.getAttribute('data-ei-src')) img.src = img.getAttribute('data-ei-src');
          img.removeAttribute('data-ei-srcset');
          img.removeAttribute('data-ei-src');
        });
      }, { root: this.wall, rootMargin: '50% 0px 50% 0px' });
    }
    Array.prototype.forEach.call(this.wall.querySelectorAll('img[data-ei-src]'), function (img) { self.lazyIO.observe(img); });
  };

  ProofWall.prototype.teardown = function () {
    this.tracks.forEach(function (t) { if (t.anim) t.anim.cancel(); });
    if (this.lazyIO) this.lazyIO.disconnect();
    if (this.colsEl) {
      Array.prototype.slice.call(this.colsEl.querySelectorAll('[data-ei-pw-clone]')).forEach(function (n) { n.parentNode.removeChild(n); });
      if (this.colsEl.parentNode) this.colsEl.parentNode.removeChild(this.colsEl);
      this.colsEl = null;
    }
    this.tracks = [];
  };

  ProofWall.prototype.bindMotion = function () {
    var self = this;
    var wall = this.wall;
    var st = this.state;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        st.visible = e.isIntersecting;
        if (e.isIntersecting && !st.started) self.start();
        self.update(true);
      });
    }, { threshold: 0.05 });
    io.observe(wall);

    document.addEventListener('visibilitychange', function () { st.hidden = document.hidden; self.update(true); });

    // Pause / play: the one control that stops the drift (hover and focus never do).
    var tg = this.toggle;
    if (tg) {
      tg.addEventListener('click', function () {
        st.paused = !st.paused;
        tg.setAttribute('aria-label', st.paused ? 'Play proof wall' : 'Pause proof wall');
        tg.classList.toggle('is-paused', st.paused);
        self.update();
      });
      tg.hidden = false;
    }

    if (reduceMq) {
      var onChange = function () {
        if (!reduceMq.matches) return;
        self.animated = false;
        self.applyRate(0);
        if (tg) tg.hidden = true;
      };
      if (reduceMq.addEventListener) reduceMq.addEventListener('change', onChange);
      else if (reduceMq.addListener) reduceMq.addListener(onChange);
    }

    // Tile sizes and the drift are percentage based, so small width changes (a scrollbar, the
    // viewer opening) need nothing. Rebuild only when the column count changes or the width
    // moves a lot (rotation, window resize), and never while the viewer is open.
    var lastW = this.root.clientWidth;
    var timer = 0;
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          var w = self.root.clientWidth;
          if (!self.animated || self.state.viewer || !w) return;
          if (self.colCount() === self.cols && Math.abs(w - lastW) / lastW < 0.15) return;
          lastW = w;
          self.buildIdle();
        }, 250);
      }).observe(this.root);
    }
  };

  ProofWall.prototype.start = function () {
    var self = this;
    this.state.started = true;
    if (this.armed) {
      requestAnimationFrame(function () {
        self.root.classList.add('is-developed');
        setTimeout(function () {
          self.root.classList.remove('is-armed', 'is-developed');
          Array.prototype.forEach.call(self.root.querySelectorAll('.ei-pw__cell'), function (c) { c.style.removeProperty('--d'); });
        }, 2800);
      });
      // Let the wave land before anything moves.
      this.holdUntil = performance.now() + 1100;
      setTimeout(function () { self.update(); }, 1150);
    }
  };

  ProofWall.prototype.shouldRun = function () {
    var s = this.state;
    return this.animated && s.started && s.visible && !s.hidden && !s.paused && !s.viewer &&
      !(this.holdUntil && performance.now() < this.holdUntil);
  };

  // hard = stop at once (off screen, hidden tab, viewer); otherwise ease (pause button).
  ProofWall.prototype.update = function (hard) {
    var run = this.shouldRun();
    this.target = run ? 1 : 0;
    if (hard && !run) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.rate = 0;
      this.applyRate(0);
      return;
    }
    if (!this.raf && this.rate !== this.target) {
      var self = this;
      var last = performance.now();
      var step = function (now) {
        var dt = Math.min(64, now - last);
        last = now;
        var delta = dt / (self.target ? 900 : 450); // gentle start, quicker stop
        self.rate = self.target > self.rate ? Math.min(self.target, self.rate + delta) : Math.max(self.target, self.rate - delta);
        self.applyRate(self.rate);
        self.raf = self.rate !== self.target ? requestAnimationFrame(step) : 0;
      };
      this.raf = requestAnimationFrame(step);
    }
  };

  ProofWall.prototype.applyRate = function (r) {
    var eased = r * r * (3 - 2 * r);
    this.tracks.forEach(function (t) {
      if (!t.anim) return;
      if (eased <= 0.001) {
        if (t.anim.playState === 'running') t.anim.pause();
      } else {
        t.anim.playbackRate = eased;
        if (t.anim.playState !== 'running') t.anim.play();
      }
    });
  };

  /* ---------------- Viewer ---------------- */

  ProofWall.prototype.initViewer = function () {
    var self = this;
    var dlg = this.dialog;
    var root = this.root;
    if (!dlg || typeof dlg.showModal !== 'function') return;
    // The viewer's images are made here, not in the HTML: they have no source until opened.
    var frame = dlg.querySelector('[data-ei-pw-frame]');
    this.lbLow = document.createElement('img');
    this.lbLow.className = 'ei-pw-lb__low';
    this.lbLow.alt = '';
    this.lbLow.width = 360;
    this.lbLow.height = 360;
    this.lbImg = document.createElement('img');
    this.lbImg.className = 'ei-pw-lb__img';
    this.lbImg.width = 1080;
    this.lbImg.height = 1080;
    this.lbImg.decoding = 'async';
    this.lbImg.setAttribute('data-ei-pw-img', '');
    frame.appendChild(this.lbLow);
    frame.appendChild(this.lbImg);
    this.lbCount = dlg.querySelector('[data-ei-pw-count]');

    this.lbImg.addEventListener('load', function () { self.lbImg.classList.add('is-ld'); });

    root.addEventListener('click', function (e) {
      var opener = e.target.closest && e.target.closest('[data-ei-pw-open]');
      if (!opener || dlg.contains(opener)) return;
      e.preventDefault();
      self.open(parseInt(opener.getAttribute('data-ei-pw-open'), 10) || 0, opener);
    });

    dlg.querySelector('[data-ei-pw-close]').addEventListener('click', function () { dlg.close(); });
    dlg.querySelector('[data-ei-pw-prev]').addEventListener('click', function () { self.go(-1); });
    dlg.querySelector('[data-ei-pw-next]').addEventListener('click', function () { self.go(1); });
    var cta = dlg.querySelector('[data-ei-pw-cta]');
    if (cta) cta.addEventListener('click', function () { dlg.close(); });

    dlg.addEventListener('click', function (e) {
      var t = e.target;
      if (t === dlg || t.hasAttribute('data-ei-pw-stage') || t.classList.contains('ei-pw-lb__bar') || t.classList.contains('ei-pw-lb__foot')) dlg.close();
    });
    dlg.addEventListener('keydown', function (e) {
      // Keep the theme's own document-level key handlers (drawers, sliders) out of the viewer;
      // one of them moves focus on Escape, which would lose the photo the visitor came from.
      if (['Escape', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].indexOf(e.key) > -1) e.stopPropagation();
      if (e.key === 'Escape') { e.preventDefault(); dlg.close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); self.go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(-1); }
      else if (e.key === 'Home') { e.preventDefault(); self.show(0); }
      else if (e.key === 'End') { e.preventDefault(); self.show(self.items.length - 1); }
    });

    // Swipe left/right on touch screens.
    var sx = 0, sy = 0, tracking = false;
    var stage = dlg.querySelector('[data-ei-pw-stage]');
    stage.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      tracking = true; sx = e.clientX; sy = e.clientY;
    });
    stage.addEventListener('pointerup', function (e) {
      if (!tracking) return;
      tracking = false;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) self.go(dx < 0 ? 1 : -1);
    });
    stage.addEventListener('pointercancel', function () { tracking = false; });

    dlg.addEventListener('close', function () {
      self.state.viewer = false;
      self.update();
      var o = self.opener;
      self.opener = null;
      if (o && document.activeElement !== o && o.isConnected) {
        try { o.focus({ preventScroll: true }); } catch (err) { o.focus(); }
      }
    });
  };

  ProofWall.prototype.open = function (i, opener) {
    if (!this.dialog || !this.items.length) return;
    this.opener = opener || null;
    this.state.viewer = true;
    this.update(true);
    this.show(i);
    if (!this.dialog.open) this.dialog.showModal();
  };

  ProofWall.prototype.go = function (step) {
    var n = this.items.length;
    this.show((this.index + step + n) % n);
  };

  ProofWall.prototype.show = function (i) {
    var n = this.items.length;
    if (!n) return;
    this.index = Math.max(0, Math.min(n - 1, i));
    var it = this.items[this.index];
    this.lbLow.src = it.s;
    this.lbImg.classList.remove('is-ld');
    this.lbImg.alt = it.a || '';
    this.lbImg.src = it.l || it.s;
    if (this.lbImg.complete && this.lbImg.naturalWidth) this.lbImg.classList.add('is-ld');
    this.lbCount.textContent = (this.data.counter || 'Photo') + ' ' + (this.index + 1) + ' of ' + n;
    [1, -1].forEach(function (d) { // warm the neighbors
      var nb = this.items[(this.index + d + n) % n];
      if (nb && nb.l) { var p = new Image(); p.decoding = 'async'; p.src = nb.l; }
    }, this);
  };

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ei-pw]'), function (root) {
      if (!root.__eiPw) root.__eiPw = new ProofWall(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
